import _ from "lodash";

import { hideUniversalLoader, showUniversalLoader } from "lib/frontend/universal-loader";
import { OpTransaction } from "lib/shared/object-pool";
import { trpc } from "utils/trpc";
import { ObjectPoolModelName, ObjectPoolModel } from "utils/types";

import { MutationSettings } from "./settings";
import { getInvalidateQueriesDependentOnModels } from "../prismaObjectSync/getInvalidateQueries";
import { useBoundQueryClient } from "../queryCache";
import { useResolveObjectPoolTransactions } from "../useTransactionalMutation";

type CreateType<T> = Omit<T, "id">;

type CreateObjectPoolRecordMutationResult<TModelName extends ObjectPoolModelName> = {
  transactions: Array<OpTransaction>;
  meta: {
    newRecord: ObjectPoolModel<TModelName>;
  };
};

export function useCreateObjectPoolRecordMutation<TModelName extends ObjectPoolModelName>(
  modelName: TModelName,
  opts: MutationSettings = {},
) {
  const boundQueryClient = useBoundQueryClient();
  const { queryClient } = boundQueryClient;
  const { companyId } = boundQueryClient;

  const resolveObjectPoolTransactions = useResolveObjectPoolTransactions();
  const invalidateQueriesDependentOnModels = getInvalidateQueriesDependentOnModels(queryClient, [
    modelName,
  ]);
  const mutationPath = `object.${_.camelCase(modelName)}.createOne`;
  const createRecordMutation = _.get(trpc, mutationPath).useMutation();

  async function mutate(
    createArgs: CreateType<ObjectPoolModel<TModelName>>,
  ): Promise<ObjectPoolModel<TModelName>> {
    // Note we can't do an optimisitc update on create - we have to wait
    // for the creation to complete so we have an ID.
    const shouldShowNavigationIndicator = opts.suppressUniversalLoader !== true;
    if (shouldShowNavigationIndicator) showUniversalLoader();
    let modelSaveResult: ObjectPoolModel<TModelName> | null = null;
    try {
      const response = (await createRecordMutation.mutateAsync({
        // Note that, for the time being we require companyId (see notes
        // on the leaky abstraction above) so we ensure it gets added
        // here.
        data: { ...createArgs, companyId } as ObjectPoolModel<TModelName>,
      })) as CreateObjectPoolRecordMutationResult<TModelName>;

      modelSaveResult = response.meta.newRecord;

      resolveObjectPoolTransactions(response.transactions, {
        skipInvalidateQueries: true,
      });

      invalidateQueriesDependentOnModels();
    } catch (err) {
      // There was no optimistic update, so nothing to unwind.
      console.error(err);
      throw err;
    }
    if (shouldShowNavigationIndicator) hideUniversalLoader();
    return modelSaveResult;
  }

  return { mutate, isMutating: createRecordMutation.isLoading };
}

export function generateBoundUseCreateObjectPoolMutation<TModelName extends ObjectPoolModelName>(
  modelName: TModelName,
) {
  function useBoundCreateObjectPoolRecord(opts: MutationSettings = {}) {
    const useMutationResult = useCreateObjectPoolRecordMutation<TModelName>(modelName, opts);
    return useMutationResult;
  }
  return useBoundCreateObjectPoolRecord;
}
